import { useContext, useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import { Heading } from "../../externalComponents/tailwind/heading";
import { Text } from "../../externalComponents/tailwind/text";
import { CustomerContext } from "../../providers";

const DialogJoinCompany = ({ isOpen, onClose, cleanInvitationUrl }) => {
  const { user } = useContext(CustomerContext);
  const [isLoading, setIsLoading] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const invitationCode = urlParams.get("invitation_code");
  const companyName = urlParams.get("company_name");

  const [hasAlreadyJoined, setHasAlreadyJoined] = useState(false);

  useEffect(() => {
    if (hasAlreadyJoined) {
      const timer = setTimeout(() => {
        setHasAlreadyJoined(false);
        onClose();
        cleanInvitationUrl();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [hasAlreadyJoined]);

  const handleJoinCompany = async () => {
    if (!invitationCode || !user?.id) {
      console.error("Missing invitation code or user ID");
      return;
    }

    setIsLoading(true);
    try {
      await api.user.signUpWithExistingEmail(user.id, {
        invitationCode: invitationCode.trim(),
      });

      cleanInvitationUrl();

      window.location.reload();
    } catch (error) {
      if (error.message.indexOf("User already joined the company") > -1) {
        setHasAlreadyJoined(true);
      } else {
        console.error("Error joining company:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      key="dialog_join_company"
      open={isOpen}
      onClose={onClose}
      className="max-h-[80vh] flex flex-col"
    >
      <DialogTitle>Join Company</DialogTitle>
      <DialogBody className="flex-1 overflow-y-auto scrollbar">
        <div className="space-y-4">
          <Heading className="text-lg">
            {companyName ? `Join "${companyName}"` : "Join Company"}
          </Heading>

          <Text>
            {companyName
              ? `You've been invited to join ${companyName}'s Simio account.`
              : "You've been invited to join a company's Simio account."}
          </Text>

          <Text className="text-sm text-gray-600">
            By joining this company, you'll have access to their campaigns,
            automations, and other features.
          </Text>

          {hasAlreadyJoined && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Text className="text-sm text-gray-600">
                You've already joined this company.
              </Text>
            </div>
          )}
        </div>
      </DialogBody>

      {!hasAlreadyJoined && (
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={onClose}
            className="min-w-[100px] h-10"
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={handleJoinCompany}
            disabled={isLoading}
            className="min-w-[100px] h-10"
          >
            {isLoading ? "Joining..." : "Join Company"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export { DialogJoinCompany };
